import React, { useEffect, useMemo, useState } from 'react';
import {
    ArrowDown, ArrowUp, CheckCircle2, CircleOff, Eye, EyeOff, Home, HelpCircle, PenLine, Plus, Search, Sparkles, Trash2, X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import ConfirmDialog from '../../components/ConfirmDialog';
import Select from '../../components/Select';
import API from '../../utils/api';
import './Faqs.css';

const HOME_LIMIT = 10; // the first 10 visible FAQs are shown on the website home page
const EMPTY_FORM = { question: '', answer: '', category: 'General', isActive: true };

const Faqs = () => {
    const [faqs, setFaqs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [categories, setCategories] = useState([]);
    const [total, setTotal] = useState(0);
    const [activeCount, setActiveCount] = useState(0);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [homeFaqIds, setHomeFaqIds] = useState([]);

    const loadFaqs = async (targetPage = 1, append = false) => {
        try {
            if (append) setLoadingMore(true);
            else setLoading(true);
            const params = { limit: 10, page: targetPage };
            if (categoryFilter !== 'all') params.category = categoryFilter;
            if (search.trim()) params.search = search.trim();
            const res = await API.get('/admin/faqs', { params });
            const incoming = (res.data.faqs || []).map((faq, index) => ({ ...faq, _position: (res.data.offset || 0) + index }));
            setFaqs(current => append ? [...current, ...incoming] : incoming);
            setCategories(res.data.categories || []);
            setTotal(res.data.total || 0);
            setActiveCount(res.data.activeCount || 0);
            setPage(res.data.page || targetPage);
            setHasMore(Boolean(res.data.hasMore));
            setHomeFaqIds(res.data.homeFaqIds || []);
        } catch (error) {
            toast.error('Failed to load FAQs');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };
    useEffect(() => { loadFaqs(); }, [categoryFilter, search]);
    const filtering = Boolean(search.trim()) || categoryFilter !== 'all';

    const visibleFaqs = faqs;

    const updateForm = (field, value) => setForm(current => ({ ...current, [field]: value }));
    const closeForm = () => { setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); };

    const startCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setShowForm(true); };
    const startEdit = faq => {
        setEditingId(faq._id);
        setForm({
            question: faq.question,
            answer: faq.answer,
            category: faq.category || 'General',
            isActive: faq.isActive,
        });
        setShowForm(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmit = async event => {
        event.preventDefault();
        if (!form.question.trim() || !form.answer.trim()) { toast.error('Question and answer are required'); return; }
        setSaving(true);
        try {
            if (editingId) {
                await API.put(`/admin/faqs/${editingId}`, form);
                toast.success('FAQ updated');
            } else {
                await API.post('/admin/faqs', form);
                toast.success('FAQ created');
            }
            closeForm();
            loadFaqs();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to save FAQ');
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async faq => {
        try {
            const res = await API.put(`/admin/faqs/${faq._id}/toggle`);
            toast.success(res.data.message);
            loadFaqs();
        } catch (error) {
            toast.error('Failed to update FAQ');
        }
    };

    const confirmDelete = async () => {
        const id = pendingDelete;
        setPendingDelete(null);
        if (!id) return;
        try {
            await API.delete(`/admin/faqs/${id}`);
            toast.success('FAQ deleted');
            loadFaqs();
        } catch (error) {
            toast.error('Failed to delete FAQ');
        }
    };

    // Moves one FAQ up or down in the full list and saves the new order.
    const move = async (faq, direction) => {
        try {
            await API.put('/admin/faqs/reorder', { id: faq._id, direction });
            loadFaqs(1);
        } catch (error) {
            toast.error('Could not save the new order');
        }
    };

    const homeCount = Math.min(activeCount, HOME_LIMIT);
    const homeFaqSet = useMemo(() => new Set(homeFaqIds), [homeFaqIds]);

    return (
        <div className="faqs-layout">
            <Sidebar />
            <div className="admin-content faqs-content">
                <Navbar title={<span className="faqs-nav-title"><HelpCircle size={18} /> FAQ management</span>} />

                <main className="faqs-page">
                    <header className="faqs-hero">
                        <div className="faqs-hero__copy">
                            <span className="faqs-eyebrow"><Sparkles size={14} /> Help centre</span>
                            <h1>Frequently asked questions</h1>
                            <p>Write the questions members and providers ask most. All visible FAQs appear on the website FAQ page, and the first 10 also appear on the home page. Use the arrows to choose which come first.</p>
                        </div>
                        <div className="faqs-hero__stats">
                            <div><strong>{total}</strong><span>Total</span></div>
                            <div><strong>{activeCount}</strong><span>Visible</span></div>
                            <div><strong>{homeCount}</strong><span>On home page</span></div>
                        </div>
                    </header>

                    <section className="faqs-toolbar">
                        <label className="faqs-search">
                            <Search size={15} />
                            <input value={search} onChange={event => setSearch(event.target.value)} placeholder="Search questions or answers" aria-label="Search FAQs" />
                            {search && <button type="button" onClick={() => setSearch('')} aria-label="Clear search"><X size={13} /></button>}
                        </label>
                        <Select value={categoryFilter} onChange={setCategoryFilter} allowEmpty={false} ariaLabel="Filter by category" options={[{ value: 'all', label: 'All categories' }, ...categories.map(category => ({ value: category, label: category }))]} />
                        <button type="button" className={`faqs-add ${showForm ? 'is-cancel' : ''}`} onClick={() => (showForm ? closeForm() : startCreate())}>
                            {showForm ? <><X size={16} /> Close</> : <><Plus size={16} /> Add FAQ</>}
                        </button>
                    </section>

                    {showForm && (
                        <form className="faq-form" onSubmit={handleSubmit}>
                            <header>
                                <span><HelpCircle size={18} /></span>
                                <div>
                                    <h2>{editingId ? 'Edit FAQ' : 'Add a new FAQ'}</h2>
                                    <p>Keep the answer short, clear and friendly. Members read these before they contact you.</p>
                                </div>
                            </header>

                            <div className="faq-form__grid">
                                <label className="faq-field faq-field--wide">
                                    <span>Question <b>*</b></span>
                                    <input value={form.question} maxLength={300} onChange={event => updateForm('question', event.target.value)} placeholder="Example: Is registration free?" required />
                                </label>
                                <label className="faq-field faq-field--wide">
                                    <span>Answer <b>*</b> <small>{form.answer.length}/4000</small></span>
                                    <textarea value={form.answer} maxLength={4000} rows={5} onChange={event => updateForm('answer', event.target.value)} placeholder="Write the answer shown to visitors" required />
                                </label>
                                <label className="faq-field">
                                    <span>Category</span>
                                    <input list="faq-categories" value={form.category} maxLength={60} onChange={event => updateForm('category', event.target.value)} placeholder="General" />
                                    <datalist id="faq-categories">{categories.map(category => <option key={category} value={category} />)}</datalist>
                                </label>
                                <div className="faq-field faq-checks">
                                    <label><input type="checkbox" checked={form.isActive} onChange={event => updateForm('isActive', event.target.checked)} /> Visible on the website</label>
                                    <small className="faq-hint">New FAQs are added at the end. Move one into the top 10 to show it on the home page.</small>
                                </div>
                            </div>

                            <footer>
                                <span><CheckCircle2 size={15} /> Changes appear on the website straight away.</span>
                                <div>
                                    <button type="button" className="secondary" onClick={closeForm}>Cancel</button>
                                    <button type="submit" className="primary" disabled={saving}>
                                        {editingId ? <PenLine size={15} /> : <Plus size={15} />} {saving ? 'Saving…' : editingId ? 'Save changes' : 'Create FAQ'}
                                    </button>
                                </div>
                            </footer>
                        </form>
                    )}

                    {filtering && total > 0 && (
                        <p className="faqs-note">Showing {visibleFaqs.length} loaded of {total} matching FAQs. Clear the search and filter to change the order.</p>
                    )}

                    {visibleFaqs.length > 0 ? (
                        <>
                            <ol className="faqs-list">
                            {visibleFaqs.map(faq => {
                                const position = faq._position;
                                return (
                                    <li key={faq._id} className={`faq-card ${faq.isActive ? '' : 'is-hidden'}`}>
                                        <span className="faq-card__num">{position + 1}</span>
                                        <div className="faq-card__body">
                                            <div className="faq-card__chips">
                                                <span className="chip chip--cat">{faq.category || 'General'}</span>
                                                {homeFaqSet.has(faq._id) && <span className="chip chip--home"><Home size={11} /> Home page</span>}
                                                <span className={`chip ${faq.isActive ? 'chip--on' : 'chip--off'}`}>
                                                    {faq.isActive ? <CheckCircle2 size={11} /> : <CircleOff size={11} />} {faq.isActive ? 'Visible' : 'Hidden'}
                                                </span>
                                            </div>
                                            <h3>{faq.question}</h3>
                                            <p>{faq.answer}</p>
                                        </div>
                                        <div className="faq-card__actions">
                                            {!filtering && (
                                                <div className="faq-card__order">
                                                    <button type="button" onClick={() => move(faq, -1)} disabled={position === 0} aria-label="Move up"><ArrowUp size={15} /></button>
                                                    <button type="button" onClick={() => move(faq, 1)} disabled={position === total - 1} aria-label="Move down"><ArrowDown size={15} /></button>
                                                </div>
                                            )}
                                            <button type="button" title="Edit" onClick={() => startEdit(faq)}><PenLine size={15} /></button>
                                            <button type="button" title={faq.isActive ? 'Hide from website' : 'Show on website'} onClick={() => toggleActive(faq)}>{faq.isActive ? <EyeOff size={15} /> : <Eye size={15} />}</button>
                                            <button type="button" title="Delete" className="danger" onClick={() => setPendingDelete(faq._id)}><Trash2 size={15} /></button>
                                        </div>
                                    </li>
                                );
                            })}
                            </ol>
                            {hasMore && <div className="faqs-load-more"><button type="button" onClick={() => loadFaqs(page + 1, true)} disabled={loadingMore}>{loadingMore ? 'Loading…' : `Load more (${Math.max(total - faqs.length, 0)} remaining)`}</button></div>}
                        </>
                    ) : !loading && (
                        <section className="faqs-empty">
                            <span><HelpCircle size={30} /></span>
                            <h2>{faqs.length === 0 ? 'No FAQs yet' : 'No FAQs match your search'}</h2>
                            <p>{faqs.length === 0 ? 'Add the first question so visitors can find answers without contacting support.' : 'Try a different word or choose another category.'}</p>
                            {faqs.length === 0 && <button type="button" onClick={startCreate}><Plus size={16} /> Add first FAQ</button>}
                        </section>
                    )}
                </main>
            </div>

            <ConfirmDialog
                open={Boolean(pendingDelete)}
                title="Delete FAQ?"
                message="This question will be permanently removed from the website."
                confirmLabel="Delete FAQ"
                danger
                onConfirm={confirmDelete}
                onCancel={() => setPendingDelete(null)}
            />
        </div>
    );
};

export default Faqs;
